import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Divider,
  LinearProgress,
  Skeleton,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockIcon from '@mui/icons-material/Lock';
import api, { festivals as festivalsApi } from '../../services/api';
import { useFestival } from '../../context/FestivalContext';
import { useAuth } from '../../context/AuthContext';

const POLL_MS = 30000;

// Single line in the stats sidebar. Long explanations live in a tooltip on
// hover so the sidebar stays scannable on smaller screens.
const StatRow = ({ label, value, to, tooltip, accent }) => {
  const row = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        py: 0.35,
        textDecoration: 'none',
        color: 'inherit',
        cursor: tooltip && !to ? 'help' : undefined,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color: accent || 'text.primary' }}
      >
        {value}
      </Typography>
    </Box>
  );

  const withLink = to ? (
    <Box
      component={RouterLink}
      to={to}
      sx={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
    >
      {row}
    </Box>
  ) : (
    row
  );

  if (!tooltip) return withLink;
  return (
    <Tooltip title={tooltip} placement="left" arrow enterTouchDelay={0}>
      {withLink}
    </Tooltip>
  );
};

const formatMoney = (cents) => {
  const dollars = (cents || 0) / 100;
  return dollars.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
};

const StatsSidebar = ({ hideReservations = false }) => {
  const { selectedId, active, refresh: refreshFestivals } = useFestival();
  const { isAdmin } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrationBusy, setRegistrationBusy] = useState(false);
  const [registrationError, setRegistrationError] = useState(null);
  const inFlight = useRef(false);

  const registrationOpen = active?.status === 'open';

  const handleToggleRegistration = useCallback(async () => {
    if (!active) return;
    const nextOpen = !registrationOpen;
    if (!nextOpen) {
      const confirmed = window.confirm(
        `Close registration for ${active.name}? Vendors will see the "registration is not yet open" message until you re-open it.`
      );
      if (!confirmed) return;
    }
    setRegistrationBusy(true);
    setRegistrationError(null);
    try {
      if (nextOpen) {
        await festivalsApi.openRegistration(active.id);
      } else {
        await festivalsApi.closeRegistration(active.id);
      }
      await refreshFestivals();
    } catch (err) {
      setRegistrationError(err.response?.data?.error || 'Failed to update registration');
    } finally {
      setRegistrationBusy(false);
    }
  }, [active, registrationOpen, refreshFestivals]);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await api.get('/admin/dashboard-summary');
      setSummary(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stats');
    } finally {
      setLoading(false);
      inFlight.current = false;
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load, selectedId]);

  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') load();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [load]);

  const boothPct = summary?.booths?.total
    ? Math.min(100, Math.round((summary.booths.booked / summary.booths.total) * 100))
    : 0;

  return (
    <Box sx={{ p: 1.5 }}>
      {isAdmin && active && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            variant="overline"
            color="text.secondary"
            sx={{ fontWeight: 700, display: 'block' }}
          >
            Registration
          </Typography>
          <Tooltip
            title={
              registrationOpen
                ? `Registration is open for ${active.name}. Click to close.`
                : `Registration is closed for ${active.name}. Click to open.`
            }
            arrow
          >
            <span>
              <Button
                size="small"
                fullWidth
                variant={registrationOpen ? 'outlined' : 'contained'}
                color={registrationOpen ? 'warning' : 'success'}
                onClick={handleToggleRegistration}
                disabled={registrationBusy}
                startIcon={registrationOpen ? <LockIcon /> : <LockOpenIcon />}
                sx={{ mt: 0.25, textTransform: 'none', fontWeight: 600 }}
              >
                {registrationOpen ? 'Close registration' : 'Open registration'}
              </Button>
            </span>
          </Tooltip>
          {registrationError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
              {registrationError}
            </Typography>
          )}
          <Divider sx={{ mt: 1 }} />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0.5,
        }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Festival Stats
        </Typography>
        <Tooltip title="Refresh stats">
          <IconButton size="small" onClick={load} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && !summary ? (
        <>
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
          <Skeleton height={28} />
        </>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : (
        <>
          <StatRow
            label="Vendors"
            value={summary.vendors.total}
            tooltip={`${summary.vendors.complete} complete · ${summary.vendors.incomplete} incomplete`}
            to="/admin/vendors"
          />
          <Tooltip
            title={`${summary.booths.available} of ${summary.booths.total} booths still available.`}
            placement="left"
            arrow
          >
            <Box sx={{ py: 0.75, cursor: 'help' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Booths
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {summary.booths.booked} / {summary.booths.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={boothPct}
                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
              />
            </Box>
          </Tooltip>
          {!hideReservations && (
            <>
              <Divider sx={{ my: 0.75 }} />
              <StatRow
                label="Pending approvals"
                value={summary.approvals.pending}
                tooltip={
                  summary.approvals.pending_stale > 0
                    ? `${summary.approvals.pending_stale} have been waiting > ${
                        summary.thresholds?.pending_nudge_days ?? 3
                      } days.`
                    : 'Vendors awaiting admin review.'
                }
                to="/admin/approvals?status=pending"
                accent={summary.approvals.pending > 0 ? 'warning.main' : undefined}
              />
              <StatRow
                label="Soft held"
                value={summary.reservations?.soft_held ?? 0}
                tooltip="Vendors actively onboarding with a booth held."
                to="/admin/registration-flow"
              />
              <StatRow
                label="Awaiting payment"
                value={summary.reservations?.approved_unpaid ?? 0}
                tooltip={
                  summary.reservations?.approved_soonest_expiry
                    ? `Soonest hold expires ${new Date(
                        summary.reservations.approved_soonest_expiry
                      ).toLocaleDateString()}.`
                    : `${
                        summary.thresholds?.approved_payment_window_days ?? 14
                      }-day payment window.`
                }
                to="/admin/payments"
                accent={summary.reservations?.approved_unpaid > 0 ? 'success.main' : undefined}
              />
              <StatRow
                label="Abandoned"
                value={summary.reservations?.profile_abandoners ?? 0}
                tooltip={`Idle > ${
                  summary.thresholds?.profile_abandon_hours ?? 24
                }h with no booth picked.`}
                to="/admin/approvals?status=incomplete"
                accent={summary.reservations?.profile_abandoners > 0 ? 'error.main' : undefined}
              />
            </>
          )}
          <Divider sx={{ my: 0.75 }} />
          <StatRow
            label="Denied"
            value={summary.approvals.denied}
            tooltip="Vendors denied for this festival."
            to="/admin/approvals?status=denied"
          />
          <Divider sx={{ my: 0.75 }} />
          <StatRow
            label="Revenue"
            value={formatMoney(summary.payments.revenue_cents)}
            tooltip={`${summary.payments.succeeded} succeeded · ${summary.payments.refunded} refunded`}
            to="/admin/payments"
            accent="success.main"
          />
        </>
      )}
    </Box>
  );
};

export default StatsSidebar;
